import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler,
} from "chart.js";
import api from "../../api";
import PanelHeading from "./PanelHeading";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const SYMBOL = { gbp: "£", usd: "$", eur: "€" };

/** Mobile Users → Payments, completed in the same dates as the dashboard. */
const paymentsLink = (params = {}) => {
  const q = new URLSearchParams({ tab: "payments", payment_status: "succeeded" });
  if (params.from) q.set("paid_from", params.from);
  if (params.to) q.set("paid_to", params.to);
  return `/admin/mobile-users?${q.toString()}`;
};

// Follows the dashboard's date range (top right) instead of its own dropdown: two
// filters on one page disagreed (29 Oct meeting).
export default function RevenueOverviewCard({ hint, params = {}, rangeLabel = "All time" }) {
  const [data, setData] = useState(null);
  const { preset, from, to } = params;

  useEffect(() => {
    let cancelled = false;
    api.get("admin/dashboard/app-users/revenue", { params: { preset, from, to } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [preset, from, to]);

  const symbol = SYMBOL[String(data?.currency || "gbp").toLowerCase()] || "£";
  const series = (data?.revenue_pence || []).map((p) => (p || 0) / 100);
  const deltaPct = Number(data?.delta_pct ?? 0);
  const up = deltaPct >= 0;

  const chartData = {
    labels: data?.labels || [],
    datasets: [{
      data: series,
      borderColor: "#7c3aed",
      backgroundColor: "rgba(124,58,237,0.12)",
      fill: true,
      tension: 0.4,
      pointRadius: 3,
    }],
  };

  return (
    <Box>
      <PanelHeading
        title="Revenue Overview"
        hint={hint}
        action={(
          <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700 }}>{rangeLabel}</Typography>
        )}
      />

      <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700, display: "block" }}>Total Revenue</Typography>
      <MuiLink
        component={RouterLink}
        to={paymentsLink(params)}
        underline="hover"
        title="See these payments"
        sx={{ display: "inline-block", color: "#111827" }}
      >
        <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {data ? data.total_display : "—"}
        </Typography>
      </MuiLink>
      <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
        {up ? (
          <TrendingUpIcon sx={{ fontSize: 14, color: "#16a34a" }} />
        ) : (
          <TrendingDownIcon sx={{ fontSize: 14, color: "#dc2626" }} />
        )}
        <Typography variant="caption" sx={{ color: up ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
          {Math.abs(deltaPct)}% {data?.delta_label || ""}
        </Typography>
      </Stack>

      <Box sx={{ height: 180, width: "100%" }}>
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${symbol}${Number(ctx.parsed.y).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: "#f1f5f9" },
                ticks: { color: "#374151", callback: (v) => `${symbol}${v}` },
              },
              x: { grid: { display: false }, ticks: { color: "#374151", maxRotation: 0, autoSkip: true } },
            },
          }}
        />
      </Box>
    </Box>
  );
}
